import { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { process } from "@progress/kendo-data-query";
import { useReactQueryHelpers } from "shared/hooks/useReactQueryHelpers";
import { UserContext } from "shared/context/UserContext";
import {
  GetDataWithCleanedDates,
  ShortDateFormat,
} from "shared/helpers/DateHelpers.js";
import { constants } from "../constants/RemittanceLookupConstants";
import { LogTrace } from "components/AppInsights/TelemetryService";

export function useRemittanceLookupLogic(
  oktaAuth,
  submittedFormData,
  setError,
  clientGridRef,
  setIsLoading,
) {
  const { invalidateReactQueryKey } = useReactQueryHelpers();
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [showUpdateEditConfirmation, setShowUpdateEditConfirmation] =
    useState(false);
  const [checkHistoryGridData, setCheckHistoryGridData] = useState([]);
  const [checkHistoryGridColumns, setCheckHistoryGridColumns] = useState([]);
  const [currentPMID, setCurrentPMID] = useState(null);
  const [currentEditPmid, setCurrentEditPmid] = useState(null);
  const [uriPmid, setUriPmid] = useState(null);
  const [
    isThereAnExistingIncompleteManualRemittance,
    setIsThereAnExistingIncompleteManualRemittance,
  ] = useState(false);

  const INITIAL_HISTORY_GRID_DATA_STATE = { skip: 0, take: 50 };
  const [checkHistoryGridDataState, setCheckHistoryGridDataState] = useState(
    INITIAL_HISTORY_GRID_DATA_STATE,
  );
  const [checkHistoryGridResultState, setCheckHistoryGridResultState] =
    useState([]);

  const userContext = useContext(UserContext);
  const navigate = useNavigate();
  const user = userContext.email;

  // Handle URL pmid parameter
  useEffect(() => {
    if (uriPmid === null) {
      let search = window.location.search;
      let params = new URLSearchParams(search);
      let pmid = params.get("pmid");
      setUriPmid(pmid);
    }
  }, []);

  // Process history grid data when it changes
  useEffect(() => {
    let processedData = process(
      checkHistoryGridData,
      INITIAL_HISTORY_GRID_DATA_STATE,
    );
    setCheckHistoryGridDataState(INITIAL_HISTORY_GRID_DATA_STATE);
    setCheckHistoryGridResultState(processedData);
  }, [checkHistoryGridData]);

  const onDataStateChange = (e) => {
    let processedData = process(checkHistoryGridData, e.dataState);
    setCheckHistoryGridDataState(e.dataState);
    setCheckHistoryGridResultState(processedData);
  };

  const handleArchiveSelected = async (selectedDataKeys) => {
    if (selectedDataKeys.length >= 15) {
      let userWantsToContinue = window.confirm(
        `You have selected ${selectedDataKeys.length} remittances to archive. Do you wish to continue?`,
      );
      if (!userWantsToContinue) return;
    }

    await archiveForPmids(selectedDataKeys);
  };

  const handleRestorePendingSelected = async (selectedDataKeys) => {
    if (selectedDataKeys.length >= 15) {
      let userWantsToContinue = window.confirm(
        `You have selected ${selectedDataKeys.length} remittances to restore to pending status. Do you wish to continue?`,
      );
      if (!userWantsToContinue) return;
    }

    await restorePendingForPmids(selectedDataKeys);
  };

  const handlePayerExcelExport = async () => {
    const payerData = userContext.locations
      .map((payer) => payer.value)
      .join(",");
    try {
      const accessToken = oktaAuth.getAccessToken();
      const response = await axios.get(
        "api/RemittanceLookup/GetRemittancePayerReport",
        {
          params: {
            userName: user,
            MMIDS: payerData,
          },
          headers: { Authorization: `Bearer ${accessToken}` },
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error exporting payer list:", error);
      setError(error.response);
      return null;
    }
  };

  const handlePayerCSVExport = async () => {
    return await handlePayerExcelExport();
  };

  const handleViewHistory = async (pmid) => {
    setCurrentPMID(pmid);
    await getArchiveHistory(pmid);
  };

  const getArchiveHistory = async (pmid) => {
    if (!pmid) return;

    try {
      const accessToken = oktaAuth.getAccessToken();
      const response = await axios.get(
        "api/RemittanceLookup/GetArchiveHistory",
        {
          params: {
            user: user,
            pmid: pmid,
          },
          headers: { Authorization: `Bearer ${accessToken}` },
        },
      );

      setArchiveHistoryData(response.data);
    } catch (error) {
      console.error("Error fetching archive history:", error);
      setArchiveHistoryData([]);
      setCheckHistoryGridColumns([]);
    }
  };

  const setArchiveHistoryData = (rows) => {
    if (rows.length > 0) {
      let headerProps = constants.archiveHistoryHeaderProps;
      const columns = [];

      Object.keys(rows[0]).forEach((key) => {
        columns.push({
          accessor: key,
          title: headerProps[key].title,
          type: headerProps[key].type,
          hidden: headerProps[key].hidden,
        });
      });

      let dataWithCleanedDates = GetDataWithCleanedDates(rows);
      setCheckHistoryGridData(dataWithCleanedDates);
      setCheckHistoryGridColumns(columns);
      setShowArchiveModal(true);
    } else {
      setShowArchiveModal(true);
      setCheckHistoryGridData([]);
    }
  };

  const archiveForPmids = async (pmids) => {
    if (pmids.length === 0) return;

    var parameters = {
      user: user,
      rpid: userContext.currentOrganization.value,
      pmids: pmids.join(","),
      archiveDateTime: ShortDateFormat(new Date()),
    };

    var apiEndpoint = "api/RemittanceLookup/ArchiveRemittance";

    //NOTE: Setting initial log level to "warning", I just want to make sure the traces are coming up
    LogTrace(
      "Remittances are archived: " + JSON.stringify(parameters),
      {
        componentName: "useRemittanceLookupLogic",
        apiEndpoint: apiEndpoint,
      },
      1,
    );

    setIsLoading(true);
    try {
      const accessToken = oktaAuth.getAccessToken();
      await axios.post(apiEndpoint, parameters, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      // Refresh the grid
      if (
        clientGridRef &&
        clientGridRef.current &&
        clientGridRef.current.RefetchGridData
      ) {
        await clientGridRef.current.RefetchGridData();
        await clientGridRef.current.UnselectAllRows();
      }
    } catch (error) {
      console.error("Error archiving remittances:", error);
      setError(error.response);
    } finally {
      setIsLoading(false);
    }
  };

  const restorePendingForPmids = async (pmids) => {
    if (pmids.length === 0) return;

    setIsLoading(true);
    try {
      const accessToken = oktaAuth.getAccessToken();
      await axios.post(
        "api/RemittanceLookup/RestorePending",
        {
          user: user,
          rpid: userContext.currentOrganization.value,
          pmids: pmids.join(","),
        },
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        },
      );
      // Refresh the grid
      if (
        clientGridRef &&
        clientGridRef.current &&
        clientGridRef.current.RefetchGridData
      ) {
        await clientGridRef.current.RefetchGridData();
        await clientGridRef.current.UnselectAllRows();
      }
    } catch (error) {
      console.error("Error restoring pending:", error);
      setError(error.response);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditManualRemittance = async (pmid) => {
    if (!pmid) return;

    try {
      const accessToken = oktaAuth.getAccessToken();
      const response = await axios.get(
        "api/ManualRemittance/GetIncompleteManualRemittanceForUserEmail",
        {
          params: {
            email: userContext.email,
            organizationId: userContext.currentOrganization.value,
            organizationType: userContext.currentOrganization.type,
          },
          headers: { Authorization: `Bearer ${accessToken}` },
        },
      );

      if (response.data && response.data.mpmPmid) {
        setIsThereAnExistingIncompleteManualRemittance(true);
      } else {
        setIsThereAnExistingIncompleteManualRemittance(false);
      }

      setCurrentEditPmid(pmid);
      setShowUpdateEditConfirmation(true);
    } catch (error) {
      console.error("Error checking for incomplete manual remittance:", error);
    }
  };

  const handleEditConfirmation = async () => {
    if (isThereAnExistingIncompleteManualRemittance) {
      setIsThereAnExistingIncompleteManualRemittance(false);
      return;
    }

    if (currentEditPmid) {
      setShowUpdateEditConfirmation(false);
      await EditPmidCallBack(currentEditPmid);
    }
  };

  const EditPmidCallBack = async (pmid) => {
    try {
      const accessToken = oktaAuth.getAccessToken();
      const response = await axios.post(
        "api/RemittanceLookup/ManualRemittanceEdit",
        {
          user: user,
          pMID: pmid,
          organizationId: userContext.currentOrganization.value,
          organizationType: userContext.currentOrganization.type,
        },
        { headers: { Authorization: `Bearer ${accessToken}` } },
      );

      alert("Manual Remittance is ready for editing...");
      navigate("/ManualRemittance", {});
    } catch (error) {
      alert("Manual Remittance failed to be created.");
      console.error("Error editing manual remittance:", error);
    }
  };

  const handleUpdatePaymentDate = async (pmid, paymentDate) => {
    setIsLoading(true);
    try {
      const accessToken = oktaAuth.getAccessToken();
      await axios.post(
        "api/RemittanceLookup/UpdatePaymentDate",
        {
          user: user,
          PaymentDate: paymentDate,
          PMID: pmid,
        },
        { headers: { Authorization: `Bearer ${accessToken}` } },
      );
      invalidateReactQueryKey(["useFetchClientGridDataDataKey"]);
    } catch (error) {
      console.error("Error updating payment date:", error);
      setError(error.response);
    } finally {
      setIsLoading(false);
    }
  };

  const handleArchiveSingle = async (pmid) => {
    await archiveForPmids([pmid]);
  };

  const handleRestorePendingSingle = async (pmid) => {
    await restorePendingForPmids([pmid]);
  };

  return {
    showArchiveModal,
    setShowArchiveModal,
    showUpdateEditConfirmation,
    setShowUpdateEditConfirmation,
    checkHistoryGridData,
    checkHistoryGridColumns,
    checkHistoryGridDataState,
    checkHistoryGridResultState,
    onDataStateChange,
    handleArchiveSelected,
    handleRestorePendingSelected,
    handlePayerExcelExport,
    handlePayerCSVExport,
    handleViewHistory,
    handleEditManualRemittance,
    handleEditConfirmation,
    handleUpdatePaymentDate,
    handleArchiveSingle,
    handleRestorePendingSingle,
    isThereAnExistingIncompleteManualRemittance,
  };
}
